import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { decodeWithBase64 } from '@/Services/encodedecode';
import { LanguageStore } from "@/Services/LanguageStore";
import { BridgeLanguage } from "@/Services/Language";
import { ipDeployer } from '@/Services/Server';

const POLL_INTERVAL_MS = 3000;

interface LogsViewerProps {
  deploymentId: string;
  onClose: () => void;
}

const LogsViewer: React.FC<LogsViewerProps> = ({ deploymentId, onClose }) => {
  const [logs, setLogs] = useState<string>('');
  const [streaming, setStreaming] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('idle'); // idle | connecting | live | stopped | reconnecting
  const [retryCount, setRetryCount] = useState<number>(0);

  const esRef = useRef<EventSource | null>(null);
  const manualStopRef = useRef<boolean>(false);
  const retryTimerRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const sseFailureRef = useRef<number>(0);

  const LanguageString = (number: any) => {
    let LangData = LanguageStore.allLanguages;
    if (Object.keys(LangData).length === 0) {
      let localLang = localStorage.getItem('BridgeLanguageStrings');
      if (localLang) {
        LangData = JSON.parse(decodeWithBase64(localLang));
      }
    }
    if (LangData && LangData[number]) {
      return LangData[number];
    }
    else {
      return BridgeLanguage(number);
    }
  }

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const stopStream = () => {
    manualStopRef.current = true;
    clearRetryTimer();
    clearPollTimer();
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setStreaming(false);
    setStatus('stopped');
  };

  const startPolling = (taskid: string, sessionid: string, macaddress: string) => {
    // Ensure SSE is stopped
    if (esRef.current) {
      try { esRef.current.close(); } catch { }
      esRef.current = null;
    }
    clearRetryTimer();
    clearPollTimer();
    manualStopRef.current = false;
    setStreaming(true);
    setStatus('polling');

    const doPoll = async () => {
      try {
        const res = await fetch(ipDeployer + '/script/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionid, macaddress, taskid })
        });
        const text = await res.text();
        setLogs(text || '');
      } catch (e) {
        setStatus('polling (error)');
      }
    };

    // immediate then interval
    doPoll();
    pollTimerRef.current = window.setInterval(doPoll, POLL_INTERVAL_MS);
  };

  const startStream = (taskid: string) => {
    // Prevent duplicate streams
    stopStream();
    manualStopRef.current = false;

    const url = `${ipDeployer}/script/${encodeURIComponent(taskid)}/stream-logs?t=${Date.now()}`;
    const es = new EventSource(url);

    setStatus('connecting');
    es.onopen = () => {
      setStatus('live');
      setRetryCount(0);
      sseFailureRef.current = 0;
    };

    es.onmessage = (e) => {
      setLogs((l) => (l ? l + '\n' + e.data : e.data));
    };

    es.onerror = () => {
      // Most often caused by proxies cutting the chunked stream. Attempt reconnect with backoff.
      if (manualStopRef.current) return; // user pressed Stop
      try { es.close(); } catch { }
      esRef.current = null;

      sseFailureRef.current += 1;
      // After 3 consecutive SSE failures, fall back to polling the logs endpoint
      if (sseFailureRef.current >= 3) {
        const task = taskid;
        // Load session/mac for polling
        let session = '';
        let mac = '';
        try {
          const raw = localStorage.getItem('userProfile');
          if (raw) {
            const user = JSON.parse(decodeWithBase64(raw));
            session = user.SessionID;
            mac = user.MACAddress;
          }
        } catch { }
        startPolling(task, session, mac);
        return;
      }

      const nextRetry = Math.min(30, Math.pow(2, Math.max(1, retryCount))); // 2s,4s,8s,... up to 30s
      setStatus(`reconnecting in ${nextRetry}s`);
      clearRetryTimer();
      retryTimerRef.current = window.setTimeout(() => {
        setRetryCount((n) => n + 1);
        startStream(taskid);
      }, nextRetry * 1000);
    };

    esRef.current = es;
    setStreaming(true);
  };

  useEffect(() => {
    const taskid = deploymentId;
    if (!taskid) return;

    // Load sessionid & macaddress for the initial logs fetch
    let sessionid = '';
    let macaddress = '';
    try {
      const raw = localStorage.getItem('userProfile');
      if (raw) {
        const user = JSON.parse(decodeWithBase64(raw));
        sessionid = user.SessionID;
        macaddress = user.MACAddress;
      }
    } catch { }

    // Prime existing logs first, then start streaming via SSE
    (async () => {
      try {
        const res = await fetch(ipDeployer + '/script/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionid, macaddress, taskid })
        });
        const text = await res.text();
        setLogs(text || '');
      } catch (e) {
        setLogs((l) => l + `\n[logs] failed to fetch initial logs: ${String(e)}\n`);
      }
      startStream(taskid);
    })();

    return () => {
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId]);

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  // Pause/resume when tab visibility changes to reduce server load
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        stopStream();
      } else {
        const taskid = deploymentId;
        if (taskid) {
          sseFailureRef.current = 0;
          clearPollTimer();
          startStream(taskid);
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold">{LanguageString("2071")}</h3>
          <div className="flex items-center space-x-3">
            <span className="text-xs text-gray-500">{status}</span>
            {streaming ? (
              <Button variant="outline" size="sm" onClick={stopStream}>
                {LanguageString("2072")}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (deploymentId) startStream(deploymentId);
                }}
              >
                {LanguageString("2073")}
              </Button>
            )}
            <Button size="sm" onClick={() => { stopStream(); onClose(); }}>
              {LanguageString("1463")}
            </Button>
          </div>
        </div>
        <div
          ref={scrollRef}
          className="p-4 h-[60vh] overflow-auto font-mono text-sm whitespace-pre-wrap bg-neutral-50"
        >
          {logs || LanguageString("2074")}
        </div>
      </div>
    </div>
  );
};

export default LogsViewer;